import { DOCUMENT } from '@angular/common';
import { ErrorHandler, Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AppInsightsService } from 'src/app/core/services/app-insights.service';
import { ClientTelemetryContextService } from 'src/app/core/services/client-telemetry-context.service';
import { AuthService } from '@auth0/auth0-angular';
import {
  getUnmatchedAuthErrorSignal,
  isLoginRequiredError,
  isInteractiveReauthRequiredError,
} from 'src/app/core/auth/reauth-error';
import { initiateInteractiveReauth } from 'src/app/core/auth/interactive-reauth';
import {
  UNICORN_CAUSE_STATE_KEY,
  UNICORN_PRODUCER_STATE_KEY,
  UnicornCause,
  UnicornProducer,
} from 'src/app/core/domain/unicorn';

const CHUNK_RELOAD_STORAGE_KEY = 'rettx.chunkLoadReloadAttempted';
const CHUNK_RELOAD_GUARD_TTL_MS = 5 * 60 * 1000;
const DYNAMIC_IMPORT_FAILURE_MESSAGES = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
];

type ChunkRecoveryAttempt = 'first' | 'first-in-progress' | 'second' | 'guard-unavailable';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private telemetryContext = inject(ClientTelemetryContextService);
  private auth = inject(AuthService);
  private document = inject(DOCUMENT);
  private chunkReloadInProgress = false;
  private navigatingToGlobalError = false;

  constructor(
    private appInsightsService: AppInsightsService,
    private router: Router,
    private zone: NgZone
  ) {}

  handleError(error: any): void {
    // If the error has been handled in the interceptor, skip logging it again.
    if (error && (error as any).__handled) {
      console.warn('Error already handled:', error);
      return;
    }

    // A `missing_refresh_token` OR an `invalid_grant` / "Unknown or invalid
    // refresh token" error is a BROKEN-but-authenticated session: force a real
    // interactive re-login (full-page /authorize) which unloads the SPA and
    // mints a fresh token WITH a refresh token. Navigating to `/login` here
    // would loop (LegacyLoginRedirect -> `/` -> publicOnlyGuard bounce).
    if (isInteractiveReauthRequiredError(error)) {
      console.warn('[GlobalErrorHandler] Broken refresh-token session, forcing interactive re-login...');
      this.zone.run(() => initiateInteractiveReauth(this.auth));
      return;
    }

    // A `login_required` error means the user is unauthenticated: navigate to the
    // `/login` route (forwards to the public landing page) — do NOT force
    // loginWithRedirect, or anonymous visitors couldn't browse the landing page.
    if (isLoginRequiredError(error)) {
      console.warn('[GlobalErrorHandler] Session expired, redirecting to login...');
      this.zone.run(() => {
        const returnUrl = this.router.url;
        this.router.navigate(['/login'], {
          queryParams: returnUrl !== '/' && returnUrl !== '/login' ? { returnUrl } : {}
        });
      });
      return;
    }

    const authClassifierMiss = getUnmatchedAuthErrorSignal(error);
    const isChunkLoadFailure = this.isChunkLoadFailure(error);
    if (isChunkLoadFailure) {
      const recoveryAttempt = this.chunkReloadInProgress
        ? 'first-in-progress'
        : this.prepareChunkRecovery();
      const selfHealReloadAttempted = recoveryAttempt === 'first';

      this.appInsightsService.trackException(
        error,
        3,
        {
          name: 'ChunkLoadError',
          cause: 'stale-build',
          producer: 'global-error-handler',
          source: 'GlobalErrorHandler',
          correlationId: this.telemetryContext.correlationId,
          route: this.getRouteName(),
          assetName: this.getAssetName(error),
          recoveryAttempt,
          selfHealReloadAttempted,
          ...(authClassifierMiss ? { authClassifierMiss } : {}),
        }
      );
      // The page may unload immediately, so make the recovery event best-effort durable.
      this.appInsightsService.flush();

      if (selfHealReloadAttempted) {
        this.chunkReloadInProgress = true;
        console.warn('[GlobalErrorHandler] Stale application chunk detected; reloading once.');
        this.document.defaultView?.location.reload();
        return;
      }
      if (recoveryAttempt === 'first-in-progress') {
        return;
      }
    } else {
      // Log the error to Application Insights.
      this.appInsightsService.trackException(
        error,
        3,
        {
          name: error?.name || 'UnknownError',
          message: error?.message,
          stack: error?.stack,
          cause: 'unknown',
          producer: 'global-error-handler',
          source: 'GlobalErrorHandler',
          // Explicitly carry the per-install correlation id (spec 034) so an
          // unhandled exception from the cold-start hang can be joined to the
          // backend 403 burst and the client auth breadcrumbs.
          correlationId: this.telemetryContext.correlationId,
          ...(authClassifierMiss ? { authClassifierMiss } : {}),
        }
      );
      // Best-effort flush so the exception survives an imminent WebView kill.
      this.appInsightsService.flush();
    }
    console.error('Global error caught:', error);

    // Run the navigation within Angular's NgZone.
    this.zone.run(() => {
      if (this.router.url !== '/global-error' && !this.navigatingToGlobalError) {
        this.navigatingToGlobalError = true;
        const cause: UnicornCause = isChunkLoadFailure
          ? 'stale-build'
          : 'unknown';
        const producer: UnicornProducer = 'global-error-handler';
        try {
          const navigation = this.router.navigate(['/global-error'], {
            state: {
              [UNICORN_CAUSE_STATE_KEY]: cause,
              [UNICORN_PRODUCER_STATE_KEY]: producer,
            }
          });
          void Promise.resolve(navigation).then(
            () => { this.navigatingToGlobalError = false; },
            () => { this.navigatingToGlobalError = false; }
          );
        } catch (navigationError) {
          this.navigatingToGlobalError = false;
          throw navigationError;
        }
      }
    });
  }

  private isChunkLoadFailure(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
      return false;
    }

    const candidate = error as { name?: unknown; message?: unknown };
    if (candidate.name === 'ChunkLoadError') {
      return true;
    }

    if (typeof candidate.message !== 'string') {
      return false;
    }

    const message = candidate.message.toLowerCase();
    return DYNAMIC_IMPORT_FAILURE_MESSAGES.some(signal => message.includes(signal));
  }

  private prepareChunkRecovery(): ChunkRecoveryAttempt {
    try {
      const storage = this.document.defaultView?.sessionStorage;
      if (!storage) {
        return 'guard-unavailable';
      }

      const now = Date.now();
      const previousAttempt = Number(storage.getItem(CHUNK_RELOAD_STORAGE_KEY));
      if (
        Number.isFinite(previousAttempt)
        && previousAttempt > 0
        && now >= previousAttempt
        && now - previousAttempt < CHUNK_RELOAD_GUARD_TTL_MS
      ) {
        return 'second';
      }
      storage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));
      return 'first';
    } catch {
      // Never reload when the one-shot guard cannot be persisted.
      return 'guard-unavailable';
    }
  }

  private getRouteName(): string {
    const firstSegment = this.router.url.split(/[?#]/, 1)[0]
      .split('/')
      .filter(Boolean)[0];
    return firstSegment && /^[a-z-]+$/i.test(firstSegment) ? `/${firstSegment}` : '/';
  }

  private getAssetName(error: unknown): string {
    const message = typeof (error as { message?: unknown })?.message === 'string'
      ? (error as { message: string }).message
      : '';
    const match = message.match(/([^/\s?#]+\.js)(?:[?#][^\s]*)?/i);
    return match?.[1] ?? 'unknown';
  }
}
